/** A field-level validation error as carried in problem+json `errors[]`. */
export interface FieldError {
  field: string;
  code?: string;
  message: string;
}

/** A technical issue for diagnostics only (schema paths, parser messages). Never shown to operators. */
export interface DiagnosticIssue {
  path: string;
  message: string;
  code?: string;
}

interface ProblemInit {
  status: number;
  method?: string;
  endpoint?: string;
  diagnostics?: DiagnosticIssue[];
  type?: string;
  title?: string;
  detail?: string;
  instance?: string;
  code?: string;
  correlationId?: string;
  errors?: FieldError[];
  raw?: unknown;
  cause?: unknown;
}

const PROBLEM_BRAND = Symbol.for('zagru.problem');

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function parseFieldErrors(v: unknown): FieldError[] {
  if (!Array.isArray(v)) return [];
  const out: FieldError[] = [];
  for (const item of v) {
    if (!isRecord(item)) continue;
    const field = asString(item.field) ?? asString(item.path) ?? asString(item.name) ?? '';
    const message = asString(item.message) ?? asString(item.detail) ?? asString(item.title) ?? '';
    const code = asString(item.code);
    out.push(code ? { field, code, message } : { field, message });
  }
  return out;
}

function defaultTitle(status: number): string {
  switch (status) {
    case 0:
      return 'Network error';
    case 400:
      return 'Bad Request';
    case 401:
      return 'Unauthorized';
    case 403:
      return 'Forbidden';
    case 404:
      return 'Not Found';
    case 409:
      return 'Conflict';
    case 422:
      return 'Unprocessable Content';
    case 429:
      return 'Too Many Requests';
    default:
      return status >= 500 ? 'Server Error' : `HTTP ${status}`;
  }
}

/**
 * RFC 9457 problem details (`application/problem+json`) surfaced as an Error.
 * Zagru extensions: `code` (stable error code such as `inventory.insufficient_available`),
 * `correlationId` and `errors[]` (field errors).
 */
export class ZagruProblem extends Error {
  readonly status: number;
  readonly type: string;
  readonly title: string;
  readonly detail?: string;
  readonly instance?: string;
  readonly code?: string;
  readonly correlationId?: string;
  readonly errors: FieldError[];
  /** Technical issues (e.g. response schema paths) for diagnostics; not user-facing. */
  readonly diagnostics: DiagnosticIssue[];
  /** HTTP method and path (without host) of the failed request. */
  method?: string;
  endpoint?: string;
  readonly raw?: unknown;
  readonly [PROBLEM_BRAND] = true;

  constructor(init: ProblemInit) {
    super(init.detail ?? init.title ?? defaultTitle(init.status), init.cause !== undefined ? { cause: init.cause } : undefined);
    this.name = 'ZagruProblem';
    this.status = init.status;
    this.type = init.type ?? 'about:blank';
    this.title = init.title ?? defaultTitle(init.status);
    this.detail = init.detail;
    this.instance = init.instance;
    this.code = init.code;
    this.correlationId = init.correlationId;
    this.errors = init.errors ?? [];
    this.diagnostics = init.diagnostics ?? [];
    this.method = init.method;
    this.endpoint = init.endpoint;
    this.raw = init.raw;
  }

  /** Builds a problem from an HTTP response and its (already parsed) body, which may be problem+json, plain JSON, text or empty. */
  static fromResponse(res: Response, body: unknown, correlationId?: string): ZagruProblem {
    const headerCorrelation = res.headers?.get?.('x-correlation-id') ?? undefined;
    const status = res.status;
    if (isRecord(body)) {
      const bodyStatus = typeof body.status === 'number' ? body.status : status;
      const errors = parseFieldErrors(body.errors ?? body.fieldErrors ?? body.violations);
      return new ZagruProblem({
        status: bodyStatus || status,
        type: asString(body.type),
        title: asString(body.title) ?? asString(body.error),
        detail: asString(body.detail) ?? asString(body.message),
        instance: asString(body.instance) ?? asString(body.path),
        code: asString(body.code) ?? asString(body.errorCode),
        correlationId: asString(body.correlationId) ?? headerCorrelation ?? correlationId,
        errors,
        raw: body,
      });
    }
    const text = typeof body === 'string' && body.trim().length > 0 ? body.trim() : undefined;
    return new ZagruProblem({
      status,
      title: res.statusText || defaultTitle(status),
      detail: text,
      correlationId: headerCorrelation ?? correlationId,
      raw: body,
    });
  }

  /** Wraps a transport-level failure (DNS, CORS, abort, timeout) as a status-0 problem with `client.*` codes. */
  static network(err: unknown, correlationId?: string): ZagruProblem {
    const name = isRecord(err) ? asString(err.name) : undefined;
    const message = err instanceof Error ? err.message : typeof err === 'string' ? err : 'Network request failed';
    const isTimeout = name === 'TimeoutError';
    const isAbort = name === 'AbortError';
    return new ZagruProblem({
      status: 0,
      type: 'about:blank',
      title: isTimeout ? 'Request timed out' : isAbort ? 'Request aborted' : 'Network error',
      detail: message,
      code: isTimeout ? 'client.timeout' : isAbort ? 'client.aborted' : 'client.network',
      correlationId,
      raw: err,
      cause: err,
    });
  }

  isValidation(): boolean {
    return this.status === 400 || this.status === 422 || this.errors.length > 0 || this.code === 'validation';
  }
  isUnauthorized(): boolean {
    return this.status === 401;
  }
  isForbidden(): boolean {
    return this.status === 403;
  }
  isNotFound(): boolean {
    return this.status === 404;
  }
  isConflict(): boolean {
    return this.status === 409;
  }
  /** Transport failure (status 0). */
  isNetwork(): boolean {
    return this.status === 0;
  }
  isServerError(): boolean {
    return this.status >= 500;
  }
  /** The server answered, but not in the shape this client was built for: a contract defect, not a user error. */
  isContractMismatch(): boolean {
    return this.code === 'client.invalid_response';
  }

  toJSON(): Record<string, unknown> {
    return {
      type: this.type,
      title: this.title,
      status: this.status,
      detail: this.detail,
      instance: this.instance,
      code: this.code,
      correlationId: this.correlationId,
      errors: this.errors,
      method: this.method,
      endpoint: this.endpoint,
    };
  }
}

/** Type guard that also recognises instances coming from another copy of the module. */
export function isZagruProblem(e: unknown): e is ZagruProblem {
  return e instanceof ZagruProblem || (isRecord(e) && (e as Record<symbol, unknown>)[PROBLEM_BRAND] === true);
}
